import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Users, Search, Download, RefreshCw, Building2, CheckCircle2,
  AlertCircle, Mail, ExternalLink, Loader2, TrendingUp, ShieldCheck, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const formatCHF = (n) =>
  new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 }).format(Math.round(n || 0));

export const AdminShareholders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ shareholders: [], summary: null });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState('');
  const [showAGSelf, setShowAGSelf] = useState(false);
  const [mailingOpen, setMailingOpen] = useState(false);
  const [mailingPreview, setMailingPreview] = useState(null);
  const [mailingForm, setMailingForm] = useState({ deadlineDays: 14, customMessage: '', pricePerShare: '' });
  const [mailingBusy, setMailingBusy] = useState(false);

  useEffect(() => {
    if (!user || !['admin', 'editor'].includes(user.role)) {
      navigate('/admin/login');
      return;
    }
    load();
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/shareholders', { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Fehler beim Laden');
      setData({ shareholders: d.shareholders || [], summary: d.summary });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshAreg = async () => {
    setRefreshing(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/areg/cache/refresh', {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Refresh fehlgeschlagen');
      toast.success('Aktienregister neu geladen');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const openMailing = async () => {
    if (!data.summary?.activeTranche) {
      toast.error('Keine aktive Tranche – Mailing nicht möglich');
      return;
    }
    // Load finance defaults if not set
    if (!mailingForm.pricePerShare) {
      try {
        const fr = await fetch(apiBase() + '/api/site/finance');
        const fd = await fr.json();
        if (fd?.success && fd.finance?.issuePrice) {
          setMailingForm((f) => ({ ...f, pricePerShare: fd.finance.issuePrice }));
        }
      } catch { /* ignore */ }
    }
    setMailingOpen(true);
    setMailingPreview(null);
    setMailingBusy(true);
    try {
      const r = await fetch(apiBase() + '/api/preemptive/admin/preview', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trancheKey: data.summary.activeTranche.pk,
          deadlineDays: mailingForm.deadlineDays,
          customMessage: mailingForm.customMessage,
          pricePerShare: mailingForm.pricePerShare ? parseFloat(mailingForm.pricePerShare) : null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Vorschau fehlgeschlagen');
      setMailingPreview(d);
      if (d?.finance?.issuePrice && !mailingForm.pricePerShare) {
        setMailingForm((f) => ({ ...f, pricePerShare: d.finance.issuePrice }));
      }
    } catch (e) {
      toast.error(e.message);
      setMailingOpen(false);
    } finally {
      setMailingBusy(false);
    }
  };

  const sendMailing = async () => {
    if (!data.summary?.activeTranche) return;
    if (!window.confirm(`Entwürfe für ${mailingPreview?.stats?.totalRecipients ?? 0} Aktionäre erstellen? Es werden noch KEINE E-Mails versendet – Sie können die Entwürfe anschliessend prüfen und freigeben.`)) return;
    setMailingBusy(true);
    try {
      const r = await fetch(apiBase() + '/api/preemptive/admin/send', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trancheKey: data.summary.activeTranche.pk,
          deadlineDays: mailingForm.deadlineDays,
          customMessage: mailingForm.customMessage,
          pricePerShare: mailingForm.pricePerShare ? parseFloat(mailingForm.pricePerShare) : null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Erstellung fehlgeschlagen');
      toast.success(`${d.created} Entwürfe erstellt${d.skipped ? `, ${d.skipped} übersprungen` : ''}. Jetzt zur Vorschau & Freigabe…`);
      setMailingOpen(false);
      navigate('/admin/preemptive/drafts');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setMailingBusy(false);
    }
  };

  const sendTestMail = async () => {
    if (!data.summary?.activeTranche) return;
    setMailingBusy(true);
    try {
      const r = await fetch(apiBase() + '/api/preemptive/admin/test-send', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trancheKey: data.summary.activeTranche.pk,
          customMessage: mailingForm.customMessage,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Test-Mail fehlgeschlagen');
      toast.success(`Test-Mail an ${d.to} gesendet`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setMailingBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data.shareholders || []).filter(s => {
      if (s.isAGSelf && !showAGSelf) return false;
      if (!q) return true;
      return (
        s.fullName?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.number?.toLowerCase().includes(q)
      );
    });
  }, [data.shareholders, query, showAGSelf]);

  const exportCSV = () => {
    const rows = [
      ['Nr.', 'Name', 'E-Mail', 'Aktien', 'Nominal CHF', 'Anteil %', 'Bezugsrecht Aktien', 'Bezugsrecht CHF', 'Plattform-Konto'],
      ...filtered.map(s => [
        s.number, s.fullName, s.email, s.shareCount,
        Math.round(s.faceValue), s.capitalPercent.toFixed(4),
        s.preemptiveRightShares, Math.round(s.preemptiveRightValue || 0),
        s.hasPlatformAccount ? 'ja' : 'nein',
      ]),
    ];
    const csv = rows.map(r =>
      r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';')
    ).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `loewenherz-aktionaere-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-[#C4A55A]/20 bg-black/80 backdrop-blur-md sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-gray-400 hover:text-[#C4A55A]" data-testid="back-to-admin">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <Building2 className="h-6 w-6 text-[#C4A55A]" />
            <h1 className="text-xl font-semibold">Aktionäre</h1>
          </div>
          <button
            onClick={refreshAreg}
            disabled={refreshing}
            data-testid="shareholders-refresh"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#C4A55A]/15 hover:bg-[#C4A55A]/25 text-[#C4A55A] text-sm disabled:opacity-50"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Aktienregister-Sync
          </button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Summary cards */}
        {data.summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <SummaryCard icon={Users} label="Aktionäre" value={data.summary.externalShareholders} testId="sum-shareholders" />
            <SummaryCard icon={TrendingUp} label="Gezeichnetes Kapital" value={`CHF ${formatCHF(data.summary.totalExternalValueCHF)}`} testId="sum-capital" />
            <SummaryCard icon={ShieldCheck} label="Mit Plattform-Konto" value={data.summary.linkedToPlatform} testId="sum-linked" />
            <SummaryCard icon={Building2} label="Inkl. AG-Eigenbestand" value={data.summary.totalShareholders} testId="sum-total" />
          </div>
        )}

        {/* Active tranche / Bezugsrecht banner */}
        {data.summary?.activeTranche && (
          <div
            data-testid="active-tranche-banner"
            className="mb-6 bg-gradient-to-r from-[#C4A55A]/15 via-[#C4A55A]/8 to-transparent border border-[#C4A55A]/40 rounded-xl p-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                <div>
                  <div className="text-sm font-semibold text-white">
                    Offene Tranche: <span className="text-[#C4A55A]">{data.summary.activeTranche.name}</span>
                  </div>
                  <div className="text-xs text-gray-400">
                    Noch verfügbar:{' '}
                    <span className="text-white">
                      {(data.summary.activeTranche.totalShares - data.summary.activeTranche.subscribedShares).toLocaleString('de-CH')} Aktien
                    </span>
                    {' · '}
                    CHF {formatCHF((data.summary.activeTranche.totalShares - data.summary.activeTranche.subscribedShares) * data.summary.activeTranche.faceValue)}
                    {' · '}
                    Bezugsrecht insgesamt: {data.summary.totalPreemptiveShares.toLocaleString('de-CH')} Aktien
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to="/admin/preemptive/drafts"
                  data-testid="goto-preemptive-drafts"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1a1a1a] border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/10 text-sm font-medium"
                >
                  <FileText className="h-4 w-4" />
                  Entwürfe & Versand
                </Link>
                <button
                  onClick={openMailing}
                  data-testid="open-preemptive-mailing"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black text-sm font-semibold transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Bezugsrechts-Mailing vorbereiten
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex-1 min-w-64 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, E-Mail oder Aktionärs-Nr. suchen…"
              data-testid="shareholders-search"
              className="w-full bg-[#1a1a1a] border border-[#C4A55A]/25 rounded-lg pl-10 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:border-[#C4A55A] focus:outline-none"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={showAGSelf}
              onChange={(e) => setShowAGSelf(e.target.checked)}
              data-testid="shareholders-toggle-self"
              className="accent-[#C4A55A]"
            />
            AG-Eigenbestand anzeigen
          </label>
          <button
            onClick={exportCSV}
            disabled={!filtered.length}
            data-testid="shareholders-export"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-sm disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            CSV-Export
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center text-gray-400 py-20">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            Lade Aktionäre…
          </div>
        ) : !filtered.length ? (
          <div className="text-center text-gray-500 py-20 bg-[#0f0f0f] rounded-xl border border-white/5">
            Keine Aktionäre gefunden.
          </div>
        ) : (
          <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#1a1a1a] text-gray-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Nr.</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">E-Mail</th>
                    <th className="px-4 py-3 text-right">Aktien</th>
                    <th className="px-4 py-3 text-right">Nominal (CHF)</th>
                    <th className="px-4 py-3 text-right">Anteil</th>
                    <th className="px-4 py-3 text-right" title="Pro-rata Bezugsrecht auf aktuelle offene Kapitalerhöhung">Bezugsrecht</th>
                    <th className="px-4 py-3 text-center">Plattform</th>
                    <th className="px-4 py-3 text-center">GAFI</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr
                      key={s.pk}
                      data-testid={`shareholder-row-${s.pk}`}
                      className={`border-t border-white/5 hover:bg-white/[0.02] ${s.isAGSelf ? 'opacity-60' : ''}`}
                    >
                      <td className="px-4 py-3 text-gray-400 font-mono">#{s.number}</td>
                      <td className="px-4 py-3 font-medium">
                        <div className="flex items-center gap-2">
                          {s.fullName}
                          {s.isAGSelf && (
                            <span className="text-[10px] uppercase tracking-wider bg-white/10 text-gray-300 px-1.5 py-0.5 rounded">
                              AG selbst
                            </span>
                          )}
                          {s.isCompany && !s.isAGSelf && (
                            <Building2 className="h-3.5 w-3.5 text-gray-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {s.email ? (
                          <a
                            href={`mailto:${s.email}`}
                            className="hover:text-[#C4A55A] inline-flex items-center gap-1"
                          >
                            <Mail className="h-3 w-3" />
                            {s.email}
                          </a>
                        ) : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {s.shareCount.toLocaleString('de-CH')}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatCHF(s.faceValue)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#C4A55A] font-semibold">
                        {s.capitalPercent.toFixed(2)}%
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {s.preemptiveRightShares > 0 ? (
                          <div>
                            <div className="text-emerald-300 font-semibold">
                              {s.preemptiveRightShares.toLocaleString('de-CH')} Aktien
                            </div>
                            <div className="text-[10px] text-gray-500">
                              CHF {formatCHF(s.preemptiveRightValue)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {s.hasPlatformAccount ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-400 inline" />
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {s.validateGafi === true ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-400 inline" />
                        ) : s.validateGafi === false ? (
                          <AlertCircle className="h-4 w-4 text-amber-400 inline" />
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 bg-[#1a1a1a]/50 text-xs text-gray-500 border-t border-white/5 flex items-center justify-between">
              <span>{filtered.length} Einträge</span>
              <a
                href="https://www.das-aktienregister.ch/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-[#C4A55A]"
              >
                Quelle: das-aktienregister.ch <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        )}
      </main>

      {/* Mailing modal */}
      {mailingOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="preemptive-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setMailingOpen(false); }}
        >
          <div className="bg-[#0f0f0f] border border-[#C4A55A]/40 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-white">Bezugsrechts-Mailing vorbereiten</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Tranche: <span className="text-[#C4A55A]">{data.summary?.activeTranche?.name}</span>
                </p>
              </div>
              <button onClick={() => setMailingOpen(false)} className="text-gray-400 hover:text-white text-2xl leading-none">×</button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <label className="block">
                <span className="text-xs text-gray-400">Annahmefrist (Tage)</span>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={mailingForm.deadlineDays}
                  onChange={(e) => setMailingForm({ ...mailingForm, deadlineDays: parseInt(e.target.value, 10) || 14 })}
                  className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white"
                  data-testid="mailing-deadline-days"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-400">Ausgabepreis pro Aktie (CHF)</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={mailingForm.pricePerShare}
                  onChange={(e) => setMailingForm({ ...mailingForm, pricePerShare: e.target.value })}
                  className="mt-1 w-full bg-black/60 border border-[#C4A55A]/40 rounded-lg px-3 py-2 text-white font-semibold"
                  data-testid="mailing-price"
                  placeholder="110.00"
                />
              </label>
              <button
                onClick={openMailing}
                disabled={mailingBusy}
                className="self-end inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#1a1a1a] border border-white/15 text-sm text-gray-200 hover:bg-white/5 disabled:opacity-50"
                data-testid="mailing-refresh-preview"
              >
                {mailingBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Vorschau aktualisieren
              </button>
            </div>

            <label className="block mb-2">
              <span className="text-xs text-gray-400">Persönlicher Zusatztext (optional)</span>
              <textarea
                rows={3}
                value={mailingForm.customMessage}
                onChange={(e) => setMailingForm({ ...mailingForm, customMessage: e.target.value })}
                className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white text-sm"
                placeholder="z.B. „Wir freuen uns, Ihnen exklusive Bezugsrechte anbieten zu können..."
                data-testid="mailing-custom-msg"
              />
            </label>
            <div className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-4">
              💡 Es werden zunächst nur <strong>Entwürfe</strong> erstellt – keine E-Mails versendet.
              Sie können danach jeden Entwurf einzeln prüfen, bearbeiten und freigeben.
            </div>

            {mailingPreview ? (
              <>
                <div className="grid grid-cols-3 gap-3 mb-4 text-center text-sm">
                  <Stat label="Empfänger" value={mailingPreview.stats.totalRecipients} />
                  <Stat label="Angebot. Aktien" value={mailingPreview.stats.totalOfferedShares.toLocaleString('de-CH')} />
                  <Stat label="Gesamtvolumen CHF" value={formatCHF(mailingPreview.stats.totalOfferedValue)} />
                </div>
                <div className="max-h-64 overflow-y-auto border border-white/5 rounded-lg mb-4">
                  <table className="w-full text-sm">
                    <thead className="bg-[#1a1a1a] text-xs text-gray-400 uppercase tracking-wider sticky top-0">
                      <tr><th className="px-3 py-2 text-left">Empfänger</th><th className="px-3 py-2 text-right">Aktien</th><th className="px-3 py-2 text-right">CHF</th></tr>
                    </thead>
                    <tbody>
                      {mailingPreview.previews.map(p => (
                        <tr key={p.shareholderPk} className="border-t border-white/5">
                          <td className="px-3 py-2">{p.fullName}<br/><span className="text-xs text-gray-500">{p.email}</span></td>
                          <td className="px-3 py-2 text-right tabular-nums">{p.offerShares}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatCHF(p.totalCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-between gap-3 flex-wrap">
                  <button
                    onClick={sendTestMail}
                    disabled={mailingBusy}
                    data-testid="mailing-test-send"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1a1a1a] border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/10 text-sm font-medium disabled:opacity-50"
                  >
                    <Mail className="h-4 w-4" />
                    Test-Mail an mich
                  </button>
                  <div className="flex gap-3">
                    <button onClick={() => setMailingOpen(false)} className="px-4 py-2 text-gray-400 hover:text-white text-sm">Abbrechen</button>
                    <button
                      onClick={sendMailing}
                      disabled={mailingBusy || !mailingPreview.previews.length}
                      data-testid="mailing-send"
                      className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold disabled:opacity-50"
                    >
                      {mailingBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                      Entwürfe erstellen ({mailingPreview.previews.length})
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center text-gray-500 py-8">
                {mailingBusy ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : 'Klick „Vorschau aktualisieren" für die Empfängerliste.'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard = ({ icon: Icon, label, value, testId }) => (
  <div
    data-testid={testId}
    className="bg-[#0f0f0f] border border-[#C4A55A]/20 rounded-xl p-4"
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-4 w-4 text-[#C4A55A]" />
      <span className="text-xs uppercase tracking-wider text-gray-400">{label}</span>
    </div>
    <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="bg-black/40 border border-white/5 rounded-lg p-3">
    <div className="text-xs text-gray-400 uppercase tracking-wider">{label}</div>
    <div className="text-lg font-bold text-white tabular-nums">{value}</div>
  </div>
);
